import { NgZone } from '@angular/core';

interface IonContentEl extends HTMLElement {
  getScrollElement?: () => Promise<HTMLElement>;
}

type ScrollHandler = (scrollEvent: Event) => void;

/**
 * Scroll listening shared by the close-on-scroll directives: while an overlay is open,
 * call `closeOverlay` on the first scroll so a `container="body"` popup — which floats
 * in a fixed position and does not track its trigger — isn't left stranded over
 * unrelated content.
 *
 * Two scroll sources are covered:
 *  - `window` (capture phase) catches light-DOM scroll containers (e.g. the table's
 *    own scroll viewport) and document scroll.
 *  - the enclosing `ion-content`'s scroll element — native scroll events are
 *    `composed: false`, so they never leave Ionic's shadow DOM to reach `window`.
 *
 * Only a scroll that can move the trigger closes: one on the document or on a container
 * holding the trigger. Scrolling inside the popup itself (a long menu) leaves it open.
 *
 * Listeners run outside Angular so scrolling never triggers change detection until we
 * close.
 */
export class CloseOnScroll {
  private listeners: Array<{ target: EventTarget; handler: ScrollHandler; capture: boolean }> = [];

  constructor(
    private readonly host: HTMLElement,
    private readonly zone: NgZone,
    private readonly isOpen: () => boolean,
    private readonly closeOverlay: () => void,
  ) {}

  attach(): void {
    this.zone.runOutsideAngular(() => {
      this.addListener(window, this.onScroll, true);

      const ionContent: IonContentEl | null = this.host.closest('ion-content');
      void ionContent?.getScrollElement?.().then((scrollEl) => {
        // The overlay may have already closed by the time this resolves.
        if (this.isOpen()) {
          this.addListener(scrollEl, this.onIonScroll, false);
        }
      });
    });
  }

  detach(): void {
    this.listeners.forEach(({ target, handler, capture }) => {
      target.removeEventListener('scroll', handler, capture);
    });
    this.listeners = [];
  }

  private readonly onScroll = (scrollEvent: Event): void => {
    // Only a container that holds the trigger moves it out from under the popup; a scroll
    // elsewhere — notably inside the popup itself — must not close it.
    const { target } = scrollEvent;
    const movesTrigger =
      target === document || !(target instanceof Node) || target.contains(this.host);
    if (movesTrigger) {
      this.closeIfOpen();
    }
  };

  // `ion-content`'s scroll element lives in its shadow root, so it never `contains()` the
  // slotted trigger — but it is the trigger's own scroller, so any scroll there moves it.
  private readonly onIonScroll = (): void => {
    this.closeIfOpen();
  };

  private closeIfOpen(): void {
    if (this.isOpen()) {
      this.zone.run(() => {
        this.closeOverlay();
      });
    }
  }

  private addListener(target: EventTarget, handler: ScrollHandler, capture: boolean): void {
    target.addEventListener('scroll', handler, capture ? { capture: true } : { passive: true });
    this.listeners.push({
      target,
      handler,
      capture,
    });
  }
}
